import React, { useState } from 'react'
//components
import { Button } from '@components/ui/buttons/Button'
import EditableTable from '@components/ui/editableTable/EditableTable'
import { ValidationErrors } from '@components/ui/messages/ValidationErrors'
import { RealChainValidationErrors } from '@components/ui/messages/RealChainValidationErrors'
import { AddRowDialog } from '../shared/AddRowDialog'
import { POIWarning } from '../shared/POIWarning'
//table config
import { AGMEditSchema } from '../tableConfig/jobControlSchemas'
import { AGMEditColumnNameMap } from "../tableConfig/jobControlColumnNameMaps"
//hooks
import { useImportExcel } from '@hooks/useImportExcel'
import useConfirmDialog from '@hooks/useConfirmDialog'
import { useExportExcel } from "@hooks/useExportExcel"
//api, utils
import { AdjustRealChainDialog } from './dialogs/AdjustRealChainDialog'
import { useAGMActions } from './hooks/useAGMActions'
import { useLoading } from '@contexts/LoadingContext'
import { useError } from '@contexts/ErrorContext'
import { getAgmTemplate } from '@services/agmsAPI'
import { AdjustRealChainTooltip } from '@components/ui/tooltips/JobControlTooltips'
import { useKmlUpload } from './hooks/useKmlUpload'

/**
 * This component will be a table where you can import AGMs, edit the info on a agm point table
 * 
 * @param {DropdownOption} selectedJob
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Job Control
 */
export const AGMEdit = ({ selectedJob, setSelectedJob }) => {
    const { 
        FileUploadButton,
        fileName,
        setFileName,
        ImportColumnsMissingList,
        setMissingColumns
    } = useImportExcel()

    const [ConfirmDialog, requestConfirm] = useConfirmDialog();
    const { handleExportToExcel } = useExportExcel()

    const {
        handleSubmit,
        data,
        setData,
        handleDeleteAllAGMs,
        handleDeleteOneAGM,
        submitAdjustRealChainClick,
        realChainValidationErrors,
        validationErrors,
        setValidationErrors,
        handleAddRowAGMEdit,
        isUnlockedWithPOIs,
        handleProcessExcelData
    } = useAGMActions(selectedJob, setSelectedJob, requestConfirm);

    const [showAdjustRealChain, setShowAdjustRealChain] = useState(false);
    const [showAddRow, setShowAddRow] = useState(false);
    const {showLoading, hideLoading} = useLoading();
    const {showError} = useError();
    const { triggerFileSelect, KmlUploadDialog } = useKmlUpload();

    const adjustRealChainClick = () => {
        setShowAdjustRealChain(true);
    }

    const handleDeleteAll = async() => {
        const isConfirmed = await requestConfirm('Are you sure you want to delete all AGMs?', 'Delete AGMs', 'Cancel');
        if (!isConfirmed) return;
        handleDeleteAllAGMs(setMissingColumns, setFileName)
    }

    const handleDownloadTemplate = async () => {
        //download logic
        showLoading('Getting Template');
        let response = await getAgmTemplate();
        if (response && response.success) {
            hideLoading();
            const link = document.createElement('a');
            link.href = response.url;
            link.setAttribute('download', ''); // Optionally specify a filename
            document.body.appendChild(link);
            link.click();
            link.remove();
        } else {
            hideLoading();
            showError(response.message);
        }     
    }


    const handleAddRowClick = async() => {
        //let them know that they may have a better experience if they use excel import instead, let them choose what to do

        const confirmationMessage = (
            <div className='text-left items-center'>
                <p className='mb-4'>You can add a single row anywhere in the table using this option.</p>

                <p className='mb-4'>If you need to add multiple rows, or want more control over your data, we recommend using Excel Import instead.
                </p>

                <p className='mb-1'>To update AGMs using Excel Import:</p>
                <ul className='mb-2'>
                    <li>1. <strong>Delete</strong> existing AGMs</li>
                    <li>2. Click <strong>Import</strong> and upload your Excel file</li>
                    <li>3. Click <strong>Submit</strong> to apply the changes</li>
                </ul>
            </div>
        );

        const isConfirmed = await requestConfirm(confirmationMessage, "Continue to Add Row", "Cancel", "primary")

        if(isConfirmed){
            setShowAddRow(true)
        }
    }

    return (
        <div className='martrak-max-width'>

            {/* template download and KML conversion - always visible */}
            <div className='flex'>
                <Button
                    variant='noBackground'
                    onClick={handleDownloadTemplate}
                    className="text-sm"
                >
                    <i className="fa-solid fa-circle-arrow-down text-purple-800 mr-2"></i>
                    Download Template
                </Button>
                <Button
                    variant='noBackground'
                    onClick={triggerFileSelect}
                    className="text-sm"
                >
                    <i className="fa-solid fa-rotate text-purple-800 mr-2"></i>
                    Convert KML/KMZ
                </Button>
            </div>

            {KmlUploadDialog}

            {/* make sure user has selected a job */}
            {selectedJob ?
                (
                    <>
                        {/* excel related buttons*/}
                        <div className='flex justify-between'>
                            <div className='flex'>
                                {/* Sept 23 - we had commented out import button because importing Real_Lat and Real_Lng with 0s can cause issues with calc chain calculations */}
                                {/* idea was to force them to import on AGM Planning for now as it avoids these errors */}

                                {/* Dec 22 - would like to remove AGM Planning page, so we are bringing this back to AGM Edit - BUT KEEP AN EYE ON THIS so we don't cause issues with calc chain */}

                                {/* import/export error messages */}
                                <FileUploadButton
                                    setData={handleProcessExcelData}
                                    data={data}
                                    route="agms"
                                    columnMap={AGMEditColumnNameMap}
                                    schema={AGMEditSchema}
                                    disabled={selectedJob.IsLocked}
                                    JobID={selectedJob.value}
                                />
                            </div>
                            <div className='flex items-center'>
                                <div>
                                    <Button
                                        variant='noBackground'
                                        onClick={adjustRealChainClick}
                                        className='text-sm'
                                        disabled={selectedJob.IsLocked}
                                    >
                                        <i className='fa-solid fa-exchange text-purple-800 mr-2'></i>
                                        Adjust Real Chain

                                    </Button>

                                    <AdjustRealChainTooltip />
                                </div>

                                <Button
                                    variant='noBackground'
                                    onClick={() => handleExportToExcel(data, AGMEditColumnNameMap, "agms", selectedJob.label, selectedJob.value)}
                                    className="text-sm ml-6"
                                >
                                    <i className="fa-solid fa-file-export text-purple-800 mr-2"></i>
                                    Export
                                </Button>


                            </div>
                        </div>

                        {showAdjustRealChain &&
                            <AdjustRealChainDialog
                                showDialog={setShowAdjustRealChain}
                                onSubmit={submitAdjustRealChainClick}
                            />
                        }

                        {showAddRow &&
                            <AddRowDialog
                                dropdownOptions={data.map((AGM, i) => {return {value: AGM.AGMID, label: AGM.Site_Name, index: i}})}
                                showDialog={setShowAddRow}
                                onSubmit={handleAddRowAGMEdit}
                                requiredFields={['Site_Name', 'Est_Chain', 'Est_Lat', 'Est_Lng']}
                                columnNameMap={AGMEditColumnNameMap}
                            />
                        }

                        {/* import error messages */}
                        <ImportColumnsMissingList />

                        {/* imported file name, if any */}
                        {fileName ? <p className='my-4 text-center italic'>{fileName}</p> : null}

                        {isUnlockedWithPOIs && <POIWarning />}

                        {/* table */}
                        <EditableTable
                            data={data}
                            setData={setData}
                            schema={AGMEditSchema}
                            canDeleteRow={true}
                            handleDeleteRow={handleDeleteOneAGM}
                            validationErrors={validationErrors}
                            setValidationErrors={setValidationErrors}
                            agmRawData={data}
                            jobIsLocked={selectedJob.IsLocked}
                        />

                        {/* submit and delete buttons */}
                        <div className='flex justify-end mb-5'>
                            {/* add a row to the bottom of the table */}
                            <Button 
                                variant="success" 
                                onClick={() => handleAddRowClick()} 
                                className='w-max'
                                disabled={selectedJob.IsLocked}
                            >
                                <i className="fa-solid fa-circle-plus mr-2"></i> 
                                Add Row
                            </Button>
                        </div>

                        <div className='flex'>
                            <Button 
                                variant="success" 
                                onClick={() => handleSubmit(setMissingColumns, setFileName)} className='w-max mr-2'
                            >
                                Submit
                            </Button>
                            <Button 
                                variant="danger" 
                                onClick={handleDeleteAll} 
                                className='w-max mr-2'
                                disabled={selectedJob.IsLocked}
                            >
                                <i className="fa-solid fa-trash-can mr-2"></i>
                                Delete All AGMs
                            </Button>
                        </div>


                        {/* error messages */}

                        {validationErrors.length > 0 && realChainValidationErrors.length === 0
                            ? <ValidationErrors validationErrors={validationErrors} columnNameMap={AGMEditColumnNameMap} />
                            : null
                        }

                        {realChainValidationErrors.length > 0
                            ? (
                                <RealChainValidationErrors
                                    validationErrors={realChainValidationErrors}
                                />

                            )
                            : null
                        }

                    </>
                )
                :
                (                
                <div className='flex justify-center mt-10'>
                    <p className='text-purple-600 font-bold italic'>Please select a job.</p>
                </div>
                )
            }

            {/* dialog box opens when "Delete All AGMs" button is clicked */}
            <ConfirmDialog />

        </div>
    )
}

